import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { printClassesStore } from "../../stores/print_mapping/print_classes_store"
import { printerStore } from "../../stores/print_mapping/printer_store"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"
import { getCsrfToken } from "shared/form_helpers"

/**
 * Reusable print class toggle panel controller.
 * - Fetches print classes when a printer is selected (via printerStore)
 * - Renders Bootstrap toggle buttons with multi-selection
 * - Provides text search/filter with clear button
 * - Updates printClassesStore with selected print class IDs
 * - Sorts print classes using natural alphanumeric ordering
 *
 * Click-to-map (Visual Print Mapper):
 * This controller owns both the selected printer (printerStore) and the selected
 * print classes (printClassesStore), so it also hosts the click-to-map behavior.
 * The floor plan icon manager emits a bubbling `floorplan:icon-clicked` custom event
 * on single click when `clickAction: 'map-print-class'`. This controller listens for
 * that event (which bubbles up to the shared root element) and creates a print
 * mapping to the selected printer with the toggled print classes.
 *
 * Used by both the card-based print mapper and the visual floor plan print mapper.
 */
export default class extends Controller {
    // printClassesStore declared first (output) so setter is available when onPrinterStoreUpdate fires
    static stores = [printClassesStore, printerStore]
    static targets = ["container", "searchInput", "emptyState", "noMatchState"]

    connect() {
        useStore(this)
        this._printClasses = []
        this._selectedIds = new Set()

        // Listen for the icon manager's click-to-map event. The event bubbles from the
        // clicked device icon up to the shared root element, so listening on the
        // controller's element (which shares the root with the icon manager) captures it.
        this._boundHandleIconClicked = this._handleIconClicked.bind(this)
        this.element.addEventListener('floorplan:icon-clicked', this._boundHandleIconClicked)
    }

    disconnect() {
        this._printClasses = []
        this._selectedIds = new Set()

        if (this._boundHandleIconClicked) {
            this.element.removeEventListener('floorplan:icon-clicked', this._boundHandleIconClicked)
        }
    }

    // Click-to-map (Visual Print Mapper)

    _handleIconClicked(event) {
        const detail = event.detail || {}
        const workstationId = detail.deviceId
        const iconEl = detail.iconElement

        if (!workstationId) return

        this._createPrintMapping(workstationId, iconEl)
    }

    async _createPrintMapping(workstationId, iconEl) {
        // Check if printer is selected
        const printer = this.printerStoreValue
        if (!printer || !printer.id) {
            sendFailureNotification('Select a printer and at least one print class before mapping')
            return
        }

        // Check if print classes are selected
        const selectedClassIds = this.printClassesStoreValue
        if (!Array.isArray(selectedClassIds) || selectedClassIds.length === 0) {
            sendFailureNotification('Select a printer and at least one print class before mapping')
            return
        }

        // Disable icon during request
        if (iconEl) {
            iconEl.style.pointerEvents = 'none'
            iconEl.style.opacity = '0.5'
        }

        try {
            const response = await fetch('/api/print_mappings/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({
                    workstation_id: workstationId,
                    printer_id: printer.id,
                    print_class_ids: selectedClassIds
                })
            })

            if (!response.ok) {
                const data = await response.json().catch(() => ({}))
                throw new Error(data.message || 'Failed to create print mapping')
            }

            const data = await response.json()
            const workstationName = data.workstation?.name || 'workstation'
            sendSuccessNotification(`Print mapping added to ${workstationName}`)

            // Notify the connection-lines controller (and any other listeners) that a
            // mapping was created for this workstation, so it can draw the line to the
            // currently selected printer. Bubbles from the shared root element.
            this.element.dispatchEvent(new CustomEvent('floorplan:mapping-created', {
                detail: { workstationId, workstation: data.workstation },
                bubbles: true
            }))
        } catch (error) {
            sendFailureNotification(error.message || 'Failed to create print mapping')
        } finally {
            // Re-enable icon
            if (iconEl) {
                iconEl.style.pointerEvents = ''
                iconEl.style.opacity = ''
            }
        }
    }

    // Store callbacks

    onPrinterStoreUpdate(printer) {
        // Clear all selections and panel content when printer changes
        this._selectedIds = new Set()
        this.setPrintClassesStoreValue([])
        this._printClasses = []

        if (printer && printer.id) {
            this._fetchPrintClasses(printer.id)
        } else {
            this._renderInstructionalMessage()
        }
    }

    // Actions

    toggleClass(event) {
        const checkbox = event.target
        const classId = checkbox.value

        if (checkbox.checked) {
            this._selectedIds.add(classId)
        } else {
            this._selectedIds.delete(classId)
        }

        // Update store with selected IDs
        this.setPrintClassesStoreValue(Array.from(this._selectedIds))
    }

    filterClasses() {
        if (!this.hasSearchInputTarget) return

        const searchTerm = this.searchInputTarget.value.toLowerCase()
        const labels = this.containerTarget.querySelectorAll('.print-class-toggle-label')
        let visibleCount = 0

        labels.forEach(label => {
            const checkbox = label.previousElementSibling
            const searchText = label.getAttribute('data-search-text') || ''

            if (searchText.includes(searchTerm)) {
                label.classList.remove('d-none')
                checkbox.classList.remove('d-none')
                visibleCount++
            } else {
                label.classList.add('d-none')
                checkbox.classList.add('d-none')
            }
        })

        // Show/hide "no matching classes" indicator
        const noMatchEl = this.containerTarget.querySelector('[data-print-mapper--print-class-toggle-panel-target="noMatchState"]')
        if (noMatchEl) {
            noMatchEl.classList.toggle('d-none', visibleCount > 0 || searchTerm.length === 0)
        }
    }

    clearFilter() {
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = ''
        }

        // Show all toggle buttons
        if (this.hasContainerTarget) {
            this.containerTarget.querySelectorAll('.print-class-toggle-label').forEach(label => {
                label.classList.remove('d-none')
                label.previousElementSibling.classList.remove('d-none')
            })
        }

        // Hide "no matching" indicator
        const noMatchEl = this.hasContainerTarget
            ? this.containerTarget.querySelector('[data-print-mapper--print-class-toggle-panel-target="noMatchState"]')
            : null
        if (noMatchEl) {
            noMatchEl.classList.add('d-none')
        }

        if (this.hasSearchInputTarget) {
            this.searchInputTarget.focus()
        }
    }

    // Private

    async _fetchPrintClasses(printerId) {
        this._renderLoading()

        try {
            const response = await fetch(`/api/print_mappings/print_classes?printer_id=${encodeURIComponent(printerId)}`)
            if (!response.ok) {
                console.error('Failed to fetch print classes')
                this._renderEmptyState()
                return
            }

            const data = await response.json()
            this._printClasses = this._naturalSort(data.print_classes || [])
            this._renderPanel()
        } catch (error) {
            console.error('Error fetching print classes:', error)
            this._renderEmptyState()
        }
    }

    _naturalSort(printClasses) {
        return [...printClasses].sort((a, b) => {
            const aName = (a.name || '').toLowerCase()
            const bName = (b.name || '').toLowerCase()

            const aParts = aName.match(/(\d+|\D+)/g) || ['']
            const bParts = bName.match(/(\d+|\D+)/g) || ['']

            for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                const ap = aParts[i] || ''
                const bp = bParts[i] || ''
                const aNum = parseInt(ap, 10)
                const bNum = parseInt(bp, 10)

                if (!isNaN(aNum) && !isNaN(bNum)) {
                    if (aNum !== bNum) return aNum - bNum
                } else {
                    if (ap < bp) return -1
                    if (ap > bp) return 1
                }
            }
            return 0
        })
    }

    _renderPanel() {
        if (!this.hasContainerTarget) return

        if (this._printClasses.length === 0) {
            this._renderEmptyState()
            return
        }

        this._hideEmptyState()

        let html = `<div class="d-flex align-items-center mb-2">
            <input type="text" class="form-control form-control-sm me-2"
                   placeholder="Search print classes..."
                   style="max-width: 250px;"
                   data-print-mapper--print-class-toggle-panel-target="searchInput"
                   data-action="input->print-mapper--print-class-toggle-panel#filterClasses">
            <button type="button" class="btn btn-sm btn-outline-secondary"
                    data-action="click->print-mapper--print-class-toggle-panel#clearFilter">Clear</button>
        </div>
        <div class="d-flex flex-wrap gap-1">`

        this._printClasses.forEach(pc => {
            const escapedName = this._escapeHtml(pc.name)
            const escapedId = this._escapeAttr(pc.id)
            const searchText = (pc.name || '').toLowerCase()
            html += `<input type="checkbox" class="btn-check print-class-toggle" id="pc_toggle_${escapedId}" value="${escapedId}" autocomplete="off"
                        data-action="change->print-mapper--print-class-toggle-panel#toggleClass">
                <label class="btn btn-outline-secondary btn-sm print-class-toggle-label" for="pc_toggle_${escapedId}" data-search-text="${this._escapeAttr(searchText)}">${escapedName}</label>`
        })

        html += '</div>'
        html += '<p class="text-muted small mt-2 d-none" data-print-mapper--print-class-toggle-panel-target="noMatchState">No matching classes</p>'

        this.containerTarget.innerHTML = html
    }

    _renderInstructionalMessage() {
        if (this.hasContainerTarget) {
            this.containerTarget.innerHTML = ''
        }
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.textContent = 'Select a printer to view available print classes'
            this.emptyStateTarget.classList.remove('d-none')
        }
    }

    _renderEmptyState() {
        if (this.hasContainerTarget) {
            this.containerTarget.innerHTML = ''
        }
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.textContent = 'No print classes available for this printer'
            this.emptyStateTarget.classList.remove('d-none')
        }
    }

    _renderLoading() {
        if (this.hasContainerTarget) {
            this.containerTarget.innerHTML = '<div class="text-center p-2"><div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Loading...</span></div></div>'
        }
        this._hideEmptyState()
    }

    _hideEmptyState() {
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.classList.add('d-none')
        }
    }

    _escapeHtml(str) {
        if (!str) return ''
        const div = document.createElement('div')
        div.textContent = str
        return div.innerHTML
    }

    _escapeAttr(str) {
        if (!str) return ''
        return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    }
}
